import React from 'react';
import * as RangeBucketsUI from './RangeBucketsUI';
import { BINNING_PRECISION, isValidAutoRangeBucketInput } from '../../utils';
import {
  setBreakdownGroupBinning,
  setSelectedBinning,
} from '../../../../../../redux/slices/oldBuilder';
import {
  IAutoRangeBinning,
  IAutoRangeBucket,
} from '../../../../../../redux/slices/oldBuilder/binning/types';
import {
  isNumberLargerThanOrEqualToZero,
  isPositiveIntegerLargerThanZero,
  isValidNumberWithinRange,
} from '../../../../../../utils/numbers';
import { useAppDispatch } from '../../../../../../redux/hooks';
import { isNil } from 'lodash';
import { MAX_INTEGER } from '../../../../../../config/constants';
import produce from 'immer';

interface IProps {
  binning: IAutoRangeBinning;
  groupIndex?: number;
}

const minErrorMessage = `Min has to be greater than 0 and less than max`;
const maxErrorMessage = `Max has to be greater than min`;
const maxBinsMessage = `Only support up to 50`;
const invalidBinsMessage = `Invalid number of bins`;

export default ({ binning, groupIndex }: IProps) => {
  const dispatch = useAppDispatch();

  const autoRangeBucket = binning.autoRangeBucket;
  const { min, max, binCount } = autoRangeBucket;

  const isValidInput = isValidAutoRangeBucketInput(min, max, binCount);
  const isTooManyBuckets =
    isPositiveIntegerLargerThanZero(binCount) && Number(binCount) > 50;

  const isMinError =
    !isNil(min) &&
    (!isValidNumberWithinRange(min, 0, !isNil(max) ? max : undefined) ||
      Number(max) <= Number(min));
  const isMaxError =
    !isNil(max) &&
    (!isValidNumberWithinRange(max, !isNil(min) ? min : 0, max) ||
      Number(max) <= Number(min));
  const isMinOrMaxError =
    !isNumberLargerThanOrEqualToZero(min) ||
    !isNumberLargerThanOrEqualToZero(max) ||
    Number(max) > MAX_INTEGER ||
    isMinError ||
    isMaxError;

  const setAutoRangeBuckets = (newRangeBucket: IAutoRangeBucket) => {
    const newBinning = produce(binning, (newBinning) => {
      newBinning.autoRangeBucket = newRangeBucket;
      delete newBinning.namedBucketDef;
    });

    if (isNil(groupIndex)) {
      dispatch(
        setSelectedBinning({
          newBinning,
        }),
      );
    } else {
      dispatch(
        setBreakdownGroupBinning({
          groupIndex,
          newBinning,
        }),
      );
    }
  };

  const getErrorMessage = () => {
    let message;

    if (isMinOrMaxError && !isNil(min) && !isNil(max)) {
      message = 'Invalid input on Min or Max value';
    } else if (!isValidInput && isTooManyBuckets) {
      message = maxBinsMessage;
    } else if (!isValidInput && binCount) {
      message = invalidBinsMessage;
    }

    if (!message) {
      return null;
    }

    return (
      <RangeBucketsUI.ErrorText $centered>{message}</RangeBucketsUI.ErrorText>
    );
  };

  return (
    <RangeBucketsUI.AutoRangeBucketsContainer>
      <RangeBucketsUI.AutoRangeBucketRow>
        <RangeBucketsUI.AutoRangeLabel>Min Value</RangeBucketsUI.AutoRangeLabel>
        <RangeBucketsUI.AutoRangeNumberInputWrapper>
          <RangeBucketsUI.AutoRangeNumberInput
            placeholder="e.g. 1"
            hasError={isMinError}
            errorMessage={minErrorMessage}
            value={min}
            precision={BINNING_PRECISION}
            onChange={(newValue: number | null) =>
              setAutoRangeBuckets({ ...autoRangeBucket, min: newValue })
            }
          />
        </RangeBucketsUI.AutoRangeNumberInputWrapper>
      </RangeBucketsUI.AutoRangeBucketRow>
      <RangeBucketsUI.AutoRangeBucketRow>
        <RangeBucketsUI.AutoRangeLabel>Max Value</RangeBucketsUI.AutoRangeLabel>
        <RangeBucketsUI.AutoRangeNumberInputWrapper>
          <RangeBucketsUI.AutoRangeNumberInput
            placeholder="e.g. 10"
            value={max}
            hasError={isMaxError}
            errorMessage={maxErrorMessage}
            precision={BINNING_PRECISION}
            onChange={(newValue: number | null) =>
              setAutoRangeBuckets({ ...autoRangeBucket, max: newValue })
            }
          />
        </RangeBucketsUI.AutoRangeNumberInputWrapper>
      </RangeBucketsUI.AutoRangeBucketRow>
      <RangeBucketsUI.AutoRangeBucketRow>
        <RangeBucketsUI.AutoRangeLabel>
          Number of Bins
        </RangeBucketsUI.AutoRangeLabel>
        <RangeBucketsUI.AutoRangeNumberInputWrapper>
          <RangeBucketsUI.AutoRangeNumberInput
            placeholder="e.g. 5"
            value={binCount}
            onChange={(newValue: number | null) =>
              setAutoRangeBuckets({
                ...autoRangeBucket,
                binCount: newValue,
              })
            }
            hasError={!isMinOrMaxError && !isValidInput && !isNil(binCount)}
            errorMessage={
              isTooManyBuckets ? maxBinsMessage : invalidBinsMessage
            }
          />
        </RangeBucketsUI.AutoRangeNumberInputWrapper>
      </RangeBucketsUI.AutoRangeBucketRow>

      {getErrorMessage()}
    </RangeBucketsUI.AutoRangeBucketsContainer>
  );
};
